import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuthStore } from "@/lib/auth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { api, tenantApi, coreApi, oauthApi } from "@/lib/api/client";
import type { OAuthProvider, PlatformLinkDetail } from "@/lib/api/client";
import { getVersionDisplay } from "@/lib/version-display";
import { BooleanIcon, VisibilitySettings as VisibilitySettingsUI } from "@nodus/ui";
import { Card, Badge, Button, TextInput, Avatar, Divider, Modal, Text, Title, Stack, Group, Box, SimpleGrid, Switch, Anchor } from "@mantine/core";
import { Unlink, Link2, Pencil, Check, X, FlaskConical, Eye } from "lucide-react";
import { useStaffView } from "@/lib/staff-view";
import { toast } from "sonner";
import { SectionGuides } from "@/components/guides/section-guides";
import { DoorPinSection } from "@/components/account/door-pin-section";

const PROVIDER_ICONS: Record<string, string> = {
  google: "/icons/google.svg",
  microsoft: "/icons/microsoft.svg",
  apple: "/icons/apple.svg",
  linkedin: "/icons/linkedin.svg",
  discord: "/icons/discord.svg",
  slack: "/icons/slack.svg",
};


/**
 * Lifted from the former /wifi page, which has redirected to /network since
 * 4272ea8f and was imported by nothing — so this card, added in #300, was
 * unreachable: a member could never see their own Wi-Fi password.
 *
 * It belongs here rather than on the Network page. This is a credential
 * belonging to the PERSON, like the door PIN beside it; the Network page
 * describes the TENANT's shared network.
 */

export default function AccountPage() {
  const { user, updateUser } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();

  // Show toast for OAuth errors/success from redirect (small delay for Toaster to mount)
  useEffect(() => {
    const error = searchParams.get("error");
    const installed = searchParams.get("installed");
    if (!error && !installed) return;
    const timer = setTimeout(() => {
      if (error === "oauth_denied") toast.info("Account linking was cancelled");
      else if (error) toast.error("Failed to link account");
      if (installed) toast.success("Slack connected");
      searchParams.delete("error");
      searchParams.delete("installed");
      setSearchParams(searchParams, { replace: true });
    }, 100);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [editingName, setEditingName] = useState(false);
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");

  const nameMutation = useMutation({
    mutationFn: (data: { first_name: string; last_name: string }) =>
      tenantApi.updateMe(data),
    onSuccess: (_resp, vars) => {
      toast.success("Name updated");
      setEditingName(false);
      updateUser({ first_name: vars.first_name, last_name: vars.last_name });
    },
    onError: () => toast.error("Failed to update name"),
  });

  const {
    data: versionInfo,
    isError: versionError,
    isLoading: versionLoading,
  } = useQuery({
    queryKey: ["core-version-info"],
    queryFn: ({ signal }) => coreApi.getVersion(signal).then((r) => r.data),
    staleTime: 60 * 1000,
    retry: 1,
  });
  const versionDisplay = getVersionDisplay(
    versionInfo?.backend_version,
    versionError,
    versionLoading,
  );

  const initials =
    [user?.first_name?.[0], user?.last_name?.[0]]
      .filter(Boolean)
      .join("")
      .toUpperCase() || "?";

  const role = user?.is_tenant_admin
    ? "Tenant Admin"
    : user?.tenant_role || "User";

  return (
    <Stack p="md" gap="lg">
      <Title order={2}>My Account</Title>
      <SectionGuides area="account" />

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
      <Card withBorder padding="lg" h="fit-content">
        <Stack align="center" gap="sm" mb="sm">
          <Avatar size="lg" radius="xl">{initials}</Avatar>
          <Stack align="center" gap={4}>
            <Title order={4}>
              {user?.first_name} {user?.last_name}
            </Title>
            <Badge variant="light">{role}</Badge>
          </Stack>
        </Stack>

        <Stack gap={0}>
          <Divider />

          <Box py="sm">
            <Text fz="sm" c="dimmed">Email</Text>
            <Text fw={500}>{user?.email}</Text>
          </Box>

          <Divider />

          <Box py="sm">
            <Group justify="space-between">
              <Text fz="sm" c="dimmed">Name</Text>
              {!editingName && (
                <Button variant="subtle" size="compact-xs" onClick={() => { setFirstName(user?.first_name || ""); setLastName(user?.last_name || ""); setEditingName(true); }}>
                  <Pencil size={12} />
                </Button>
              )}
            </Group>
            {editingName ? (
              <Group gap="xs" mt={4}>
                <TextInput value={firstName} onChange={(e) => setFirstName(e.currentTarget.value)} placeholder="First name" size="xs" flex={1} />
                <TextInput value={lastName} onChange={(e) => setLastName(e.currentTarget.value)} placeholder="Last name" size="xs" flex={1} />
                <Button size="compact-xs" disabled={nameMutation.isPending} onClick={() => nameMutation.mutate({ first_name: firstName, last_name: lastName })}>
                  <Check size={14} />
                </Button>
                <Button variant="subtle" size="compact-xs" onClick={() => setEditingName(false)}>
                  <X size={14} />
                </Button>
              </Group>
            ) : (
              <Text fw={500}>
                {user?.first_name} {user?.last_name}
              </Text>
            )}
          </Box>

          <Divider />

          <Box py="sm">
            <Text fz="sm" c="dimmed">Tenant</Text>
            <Text fw={500}>{user?.tenant_name}</Text>
          </Box>

          <Divider />

          <Box py="sm">
            <Text fz="sm" c="dimmed">Site</Text>
            <Text fw={500}>{user?.site_name}</Text>
          </Box>

          <Divider />

          <Box py="sm">
            <Text fz="sm" c="dimmed">Role</Text>
            <Text fw={500}>{role}</Text>
          </Box>
        </Stack>
      </Card>

        <Stack gap="md">
          <ConnectedAccounts />
          <DoorPinSection />
          <VisibilitySettingsSection />
          <BetaProgramSection />
          <StaffViewSection />
          <DataExport />
        </Stack>
      </SimpleGrid>

      {/* Version links to the release notes. */}
      <Anchor
        component={Link}
        to="/changelog"
        ta="center"
        fz="xs"
        c="dimmed"
        underline="hover"
        title={versionDisplay.tooltip}
      >
        {versionDisplay.text}
      </Anchor>
    </Stack>
  );
}

function DataExport() {
  const downloadMutation = useMutation({
    mutationFn: async () => {
      const response = await api.get("/tenant-portal/gdpr-export/", {
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        response.headers["content-disposition"]
          ?.split("filename=")[1]
          ?.replace(/"/g, "") || "nodus-gdpr-export.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    onSuccess: () => toast.success("Data export downloaded"),
    onError: (err) => {
      Sentry.captureException(err);
      toast.error("Failed to download data export");
    },
  });

  return (
    <Card withBorder padding="lg">
      <Stack gap="xs" mb="sm">
        <Text fw={500}>Your Data</Text>
        <Text fz="xs" c="dimmed">
          Download a copy of all your personal data (GDPR)
        </Text>
      </Stack>
      <Button
        variant="outline"
        size="sm"
        onClick={() => downloadMutation.mutate()}
        disabled={downloadMutation.isPending}
      >
        {downloadMutation.isPending ? "Preparing..." : "Download my data"}
      </Button>
    </Card>
  );
}

function VisibilitySettingsSection() {
  const { user, updateUser } = useAuthStore();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (newHiddenFrom: string[]) =>
      tenantApi.updateMe({ hidden_from: newHiddenFrom }).then((r) => r.data),
    onSuccess: (data) => {
      updateUser({ hidden_from: data.hidden_from });
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
    onError: () => toast.error("Failed to save visibility settings"),
  });

  return (
    <Card withBorder padding="lg">
      <VisibilitySettingsUI
        show={["doorbell"]}
        hiddenFrom={user?.hidden_from ?? []}
        onUpdate={(h) => mutation.mutate(h)}
        disabled={mutation.isPending}
        title="Visibility"
        subtitle="Control where you appear in the building"
      />
    </Card>
  );
}

function StaffViewSection() {
  const { user } = useAuthStore();
  const { viewAsMember, setViewAsMember } = useStaffView();

  // Staff-only "god mode" switch — never rendered for regular members.
  if (!user?.is_staff) return null;

  return (
    <Card withBorder padding="lg">
      <Group gap="xs" mb="xs">
        <Eye size={16} />
        <Text fw={500}>Staff view</Text>
      </Group>
      <Text fz="xs" c="dimmed" mb="md">
        As Nodus staff you see every feature-gated menu by default. Flip this to
        browse the portal exactly as a regular member sees it.
      </Text>
      <Switch
        checked={viewAsMember}
        onChange={(e) => setViewAsMember(e.currentTarget.checked)}
        label={viewAsMember ? "Viewing as a regular member" : "Showing all features"}
      />
    </Card>
  );
}


function BetaProgramSection() {
  const { user, updateUser } = useAuthStore();
  const queryClient = useQueryClient();
  const isBeta = user?.is_beta_tester ?? false;

  const mutation = useMutation({
    mutationFn: (next: boolean) =>
      tenantApi.updateMe({ is_beta_tester: next }).then((r) => r.data),
    onSuccess: (data) => {
      updateUser({ is_beta_tester: data.is_beta_tester });
      queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.success(
        data.is_beta_tester ? "You've joined the beta program" : "You've left the beta program",
      );
    },
    onError: (err) => {
      Sentry.captureException(err);
      toast.error("Failed to update beta settings");
    },
  });

  return (
    <Card withBorder padding="lg">
      <Group gap="xs" mb="xs">
        <FlaskConical size={16} />
        <Text fw={500}>Beta program</Text>
      </Group>
      <Text fz="xs" c="dimmed" mb="md">
        Get early access to new features while we&apos;re still testing them. Beta
        features may change, behave unexpectedly, or disappear before launch — and
        we&apos;d love your feedback. You can opt out anytime.
      </Text>
      <Switch
        checked={isBeta}
        onChange={(e) => mutation.mutate(e.currentTarget.checked)}
        disabled={mutation.isPending}
        label={isBeta ? "You're in the beta program" : "Join the beta program"}
      />
    </Card>
  );
}

function ConnectedAccounts() {
  const queryClient = useQueryClient();
  const [unlinkTarget, setUnlinkTarget] = useState<{ platform: string; platformId: string; label: string; displayName: string } | null>(null);

  const { data: providers = [] } = useQuery({
    queryKey: ["oauth-providers"],
    queryFn: () => oauthApi.getProviders().then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: links = [],
    isError: linksError,
    error: linksQueryError,
  } = useQuery({
    queryKey: ["my-platform-links"],
    queryFn: async () => {
      const res = await api.get<PlatformLinkDetail[]>(
        "/tenant-portal/my-platform-links/",
      );
      return res.data;
    },
  });

  useEffect(() => {
    if (linksQueryError) Sentry.captureException(linksQueryError);
  }, [linksQueryError]);

  const unlinkMutation = useMutation({
    mutationFn: async ({
      platform,
      platformId,
    }: {
      platform: string;
      platformId: string;
    }) => {
      await api.delete("/tenant-portal/platform-link/", {
        params: { platform, platform_id: platformId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-platform-links"] });
      toast.success("Account disconnected");
      setUnlinkTarget(null);
    },
    onError: (err) => {
      Sentry.captureException(err);
      toast.error("Failed to disconnect account. Please try again.");
    },
  });

  const handleConnect = async (provider: OAuthProvider) => {
    try {
      const resp = await api.get(`/tenant-portal/oauth/${provider.provider}/link/`);
      window.location.href = resp.data.url;
    } catch {
      toast.error("Failed to start account linking");
    }
  };

  const linksByPlatform = links.reduce<Record<string, PlatformLinkDetail[]>>(
    (acc, link) => {
      (acc[link.platform] ??= []).push(link);
      return acc;
    },
    {},
  );

  if (providers.length === 0 && links.length === 0) return null;

  return (
    <>
      <Card withBorder padding="lg">
        <Stack gap="xs" mb="sm">
          <Text fw={500}>Connected Accounts</Text>
          <Text fz="xs" c="dimmed">
            Sign in with any connected account
          </Text>
        </Stack>
        <Stack gap={0}>
          {linksError && (
            <Text fz="xs" c="red" py="xs">
              Failed to load connected accounts.
            </Text>
          )}
          {providers.map((provider) => {
            const providerLinks = linksByPlatform[provider.provider] ?? [];
            const iconSrc = PROVIDER_ICONS[provider.icon];

            return (
              <div key={provider.provider}>
                <Divider />
                {providerLinks.length > 0 ? (
                  providerLinks.map((link) => (
                    <Group
                      key={link.platform_id}
                      justify="space-between"
                      py="sm"
                    >
                      <Group gap="xs">
                        {iconSrc ? (
                          <img src={iconSrc} alt="" style={{ height: 16, width: 16 }} />
                        ) : null}
                        <div>
                          <Text fz="sm" fw={500}>{provider.label}</Text>
                          <Text fz="xs" c="dimmed">
                            <BooleanIcon value={true} size="sm" />
                            {link.display_name || link.user_name}
                            {link.email ? ` (${link.email})` : ""}
                          </Text>
                          {provider.provider === "slack" && (
                            <Text fz="xs" c="dimmed" mt={2}>
                              Enables Slack chatbot features
                            </Text>
                          )}
                        </div>
                      </Group>
                      <Button
                        variant="subtle"
                        size="compact-sm"
                        disabled={unlinkMutation.isPending}
                        onClick={() => setUnlinkTarget({
                          platform: link.platform,
                          platformId: link.platform_id,
                          label: provider.label,
                          displayName: link.display_name || link.email,
                        })}
                      >
                        <Unlink size={12} />
                      </Button>
                    </Group>
                  ))
                ) : (
                  <Group justify="space-between" py="sm">
                    <Group gap="xs">
                      {iconSrc ? (
                        <img
                          src={iconSrc}
                          alt=""
                          style={{ height: 16, width: 16, opacity: 0.5 }}
                        />
                      ) : null}
                      <div>
                        <Text fz="sm" fw={500} c="dimmed">
                          {provider.label}
                        </Text>
                        <Text fz="xs" c="dimmed">
                          Not connected
                        </Text>
                      </div>
                    </Group>
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={() => handleConnect(provider)}
                    >
                      <Link2 size={12} style={{ marginRight: 4 }} />
                      Connect
                    </Button>
                  </Group>
                )}
              </div>
            );
          })}
        </Stack>
      </Card>

      <Modal
        opened={!!unlinkTarget}
        onClose={() => setUnlinkTarget(null)}
        title={`Disconnect ${unlinkTarget?.label}?`}
      >
        <Text fz="sm" c="dimmed" mb="lg">
          This will remove the connection to your{" "}
          {unlinkTarget?.label} account (
          {unlinkTarget?.displayName}). You can reconnect it later.
        </Text>
        <Group justify="flex-end">
          <Button variant="outline" onClick={() => setUnlinkTarget(null)}>Cancel</Button>
          <Button
            onClick={() => {
              if (unlinkTarget) {
                unlinkMutation.mutate({
                  platform: unlinkTarget.platform,
                  platformId: unlinkTarget.platformId,
                });
              }
            }}
          >
            Disconnect
          </Button>
        </Group>
      </Modal>
    </>
  );
}
